import { BarChart3 } from "lucide-react";
import { PageFooter } from "@/components/PageFooter";
import { useLang } from "@/i18n";

/**
 * Country-scoped dashboard view.
 *
 * Renders an embedded QuickSight iframe for the logged-in country owner.
 * Visibility is enforced by App.js (only users whose profile carries a
 * `dashboardUrl` can reach this view).
 */
export const CountryDashboard = ({ profile }) => {
  const { t } = useLang();
  if (!profile || !profile.dashboardUrl) {
    return (
      <div className="bg-white border border-[#e8e8e8] rounded-sm p-8" data-testid="country-dashboard-empty">
        <p className="text-sm text-[#555555]">
          {t("No dashboard is configured for your account.")}
        </p>
      </div>
    );
  }

  return (
    <div data-testid="country-dashboard">
      {/* Header */}
      <div className="mb-8">
        <p className="cchbc-eyebrow mb-3 text-[#dd260e]" data-testid="country-dashboard-eyebrow">
          {profile.countryLabel} · {t("Private Dashboard")}
        </p>
        <h1 className="font-condensed text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-[#000000] mb-3">
          {profile.dashboardLabel}
        </h1>
        <p className="text-sm text-[#555555] max-w-2xl">
          {t("A dedicated QuickSight sheet prepared for")}{" "}
          <strong className="text-[#000000]">{profile.countryLabel}</strong>{" "}
          {t("Country Owners. Filter, drill down and export the data you need for local decision-making.")}
        </p>
      </div>

      {/* Embed */}
      <div className="bg-white border border-[#e8e8e8] rounded-sm overflow-hidden" data-testid="country-dashboard-frame-wrap">
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#e8e8e8] bg-[#fafafa]">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-sm bg-[#000000] flex items-center justify-center">
              <BarChart3 className="w-3.5 h-3.5 text-white" strokeWidth={2} />
            </div>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#000000]">
                AWS QuickSight
              </p>
              <p className="text-[10px] text-[#555555]">
                {t("Embedded ·")} {profile.countryLabel}
              </p>
            </div>
          </div>
          <a
            href={profile.dashboardUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] font-bold uppercase tracking-[0.12em] text-[#dd260e] hover:underline"
            data-testid="country-dashboard-open-new-tab"
          >
            {t("Open in new tab")}
          </a>
        </div>
        <div className="w-full" style={{ aspectRatio: "4 / 3" }}>
          <iframe
            title={`${profile.countryLabel} dashboard`}
            src={profile.dashboardUrl}
            className="w-full h-full block"
            allow="fullscreen"
            data-testid="country-dashboard-iframe"
          />
        </div>
      </div>

      {/* Confidentiality note */}
      <p className="mt-4 text-[10px] text-[#555555] leading-relaxed max-w-2xl">
        {t("Confidential — Internal use only. Data is provided exclusively to")}{" "}
        {profile.countryLabel} {t("Country Owners. Do not redistribute outside the Coca-Cola HBC and Master F&B teams.")}
      </p>

      <PageFooter />
    </div>
  );
};
